import React, { useEffect, useMemo, useState } from "react";
import { RotateCw, Maximize2, RotateCcw, X, Volume1, Volume2, VolumeX, Sparkles, ChevronDown, Type, AlignLeft, AlignCenter, AlignRight, Loader2, Wand2, Video, CheckCircle2, Image, Music, Camera } from "lucide-react";
import type { Clip, ImageMask } from "../../types";
import { ImageMaskSelectionModal } from "./ImageMaskSelectionModal";
import { computeMaskOverlayTransform } from "../../utils/image-mask-placement";

interface TransformControlsProps {
    clip: Clip;
    onClose: () => void;
    onTransformChange?: (clipId: string, updates: Partial<Clip>) => void;
    onVolumeChange?: (volume: number) => void;
    onVolumeCommit?: (volume: number) => void;
    onEnhanceAudio?: (clip: Clip) => void;
    onSelectAudioSource?: (clip: Clip, sourceType: 'original' | 'enhanced', sourceId?: string) => void;
    onDeleteAudioSource?: (clip: Clip, sourceId: string) => void;
    onEnhanceVideo?: (clip: Clip, prompt: string) => void;
    onCancelEnhanceVideo?: (clip: Clip) => void;
    onSelectVideoVersion?: (clip: Clip, versionId: string | undefined) => void;

    // Image Mask (Nano Banana)
    onGenerateImageMask?: (clip: Clip, prompt: string, useActiveFrame?: boolean | Blob, cropData?: ImageMask['cropRect']) => void;
    onCaptureFrameForMask?: (clip: Clip, useActiveFrame?: boolean) => Promise<Blob | null>;
    onToggleImageMask?: (clip: Clip, maskId: string, active: boolean) => void;
    onDeleteImageMask?: (clip: Clip, maskId: string) => void;
    onAddImageMaskToTimeline?: (clip: Clip, mask: ImageMask) => void;

    allClips?: Clip[];
}

// Preset prompts for video enhancement
const VIDEO_ENHANCEMENT_PRESETS = [
    { id: 'remove-mic', label: '🎤 Supprimer micro', prompt: 'remove the microphone from the video' },
    { id: 'remove-logo', label: '🏷️ Supprimer logo', prompt: 'remove the logo from the video' },
    { id: 'remove-object', label: '📦 Supprimer objet', prompt: 'remove the unwanted object from the video' },
    { id: 'remove-glasses-reflection', label: '👓 Reflets lunettes', prompt: 'remove the reflection on the glasses' },
    { id: 'remove-stray-hair', label: '💇 Cheveux fous', prompt: 'remove the stray hairs' },
    { id: 'remove-lens-flare', label: '✨ Lens flare', prompt: 'remove the lens flare' },
    { id: 'change-background', label: '🖼️ Changer fond', prompt: 'change the background to a professional office setting' },
];

const TransformControls: React.FC<TransformControlsProps> = ({
    clip,
    onClose,
    onTransformChange,
    onVolumeChange,
    onVolumeCommit,
    onEnhanceAudio,
    onSelectAudioSource,
    onDeleteAudioSource,
    onEnhanceVideo,
    onCancelEnhanceVideo,
    onSelectVideoVersion,

    onGenerateImageMask,
    onCaptureFrameForMask,
    onToggleImageMask,
    onDeleteImageMask,
    onAddImageMaskToTimeline,

    allClips,
}) => {
    const isZoomClip = clip.type === 'zoom';
    const currentTransform = isZoomClip ? clip.endTransform : clip.transform;
    const isTextClip = clip.type === 'text';

    const [localScale, setLocalScale] = useState(currentTransform?.scale ?? 1);
    const [localRotation, setLocalRotation] = useState(currentTransform?.rotation ?? 0);
    const [localPositionX, setLocalPositionX] = useState(currentTransform?.x ?? 0);
    const [localPositionY, setLocalPositionY] = useState(currentTransform?.y ?? 0);
    const [localVolume, setLocalVolume] = useState(clip.volume ?? 1);
    const [localPlaybackRate, setLocalPlaybackRate] = useState(clip.playbackRate ?? 1);
    const [showAudioSources, setShowAudioSources] = useState(true);

    // Video enhancement state
    const [customVideoPrompt, setCustomVideoPrompt] = useState('');
    const [showVideoEnhancement, setShowVideoEnhancement] = useState(false);
    const [showVideoVersions, setShowVideoVersions] = useState(false);

    // Image Mask state
    const [imageMaskPrompt, setImageMaskPrompt] = useState('');
    const [showImageMasks, setShowImageMasks] = useState(false);

    // Modal state for Nano Banana selection
    const [maskSelectionData, setMaskSelectionData] = useState<{ isOpen: boolean; blob: Blob | null; prompt: string; clipId: string | null }>({
        isOpen: false,
        blob: null,
        prompt: "",
        clipId: null
    });

    // Text properties
    const [localText, setLocalText] = useState(clip.text || '');
    const [localFontSize, setLocalFontSize] = useState(clip.style?.fontSize || 50);
    const [localColor, setLocalColor] = useState(clip.style?.color || '#FFFFFF');
    const [localFontFamily, setLocalFontFamily] = useState(clip.style?.fontFamily || 'Arial, sans-serif');
    const [localTextAlign, setLocalTextAlign] = useState<'left' | 'center' | 'right'>(clip.style?.textAlign || 'center');

    // Scrubbing state
    const [isDragging, setIsDragging] = useState<'x' | 'y' | null>(null);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragStartValue, setDragStartValue] = useState(0);

    useEffect(() => {
        setLocalScale(currentTransform?.scale ?? 1);
        setLocalRotation(currentTransform?.rotation ?? 0);
        setLocalPositionX(currentTransform?.x ?? 0);
        setLocalPositionY(currentTransform?.y ?? 0);
    }, [clip.id, currentTransform]);

    useEffect(() => {
        setLocalVolume(clip.volume ?? 1);
        setLocalPlaybackRate(clip.playbackRate ?? 1);
    }, [clip.id, clip.volume, clip.playbackRate]);

    useEffect(() => {
        if (isTextClip) {
            setLocalText(clip.text || '');
            setLocalFontSize(clip.style?.fontSize || 50);
            setLocalColor(clip.style?.color || '#FFFFFF');
            setLocalFontFamily(clip.style?.fontFamily || 'Arial, sans-serif');
            setLocalTextAlign(clip.style?.textAlign || 'center');
        }
    }, [clip.id, clip.text, clip.style, isTextClip]);

    // Scrubbing handlers
    const handleScrubStart = (axis: 'x' | 'y', e: React.MouseEvent) => {
        setIsDragging(axis);
        setDragStartX(e.clientX);
        setDragStartValue(axis === 'x' ? localPositionX : localPositionY);
        document.body.style.cursor = 'ew-resize';
    };

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const delta = (e.clientX - dragStartX) * 1; // 1px per 1px drag
            const newValue = Math.round(dragStartValue + delta);
            if (isDragging === 'x') {
                setLocalPositionX(newValue);
                handlePositionChange(newValue, localPositionY);
            } else {
                setLocalPositionY(newValue);
                handlePositionChange(localPositionX, newValue);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(null);
            document.body.style.cursor = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragStartX, dragStartValue, localPositionX, localPositionY]);

    const VolumeIcon = useMemo(() => {
        if (localVolume > 1) return Volume2;
        if (localVolume > 0) return Volume1;
        return VolumeX;
    }, [localVolume]);

    const isAudioClip = useMemo(() => {
        if (clip.type === 'audio') return true;
        if (clip.type !== 'video') return false;
        const url = clip.url || '';
        const isAnimated = url.toLowerCase().endsWith('.gif') || url.toLowerCase().endsWith('.png');
        return !isAnimated;
    }, [clip]);

    const handlePositionChange = (x: number, y: number) => {
        if (currentTransform && onTransformChange) {
            const newTransform = { ...currentTransform, x, y };
            const update = isZoomClip ? { endTransform: newTransform } : { transform: newTransform };
            onTransformChange(clip.id, update);
        }
    };

    const handleScaleChange = (value: number) => {
        setLocalScale(value);
        if (currentTransform && onTransformChange) {
            const newTransform = { ...currentTransform, scale: value };
            const update = isZoomClip ? { endTransform: newTransform } : { transform: newTransform };
            onTransformChange(clip.id, update);
        }
    };

    const handleRotationChange = (value: number) => {
        setLocalRotation(value);
        if (currentTransform && onTransformChange) {
            const newTransform = { ...currentTransform, rotation: value };
            const update = isZoomClip ? { endTransform: newTransform } : { transform: newTransform };
            onTransformChange(clip.id, update);
        }
    };

    const handleReset = () => {
        if (currentTransform && onTransformChange) {
            const newTransform = { ...currentTransform, scale: 1, rotation: 0, x: 0, y: 0 };
            const update = isZoomClip ? { endTransform: newTransform } : { transform: newTransform };
            onTransformChange(clip.id, update);
            setLocalScale(1);
            setLocalRotation(0);
            setLocalPositionX(0);
            setLocalPositionY(0);
        }
    };

    const handleVolumeChange = (value: number) => {
        setLocalVolume(value);
        onVolumeChange?.(value);
    };

    const handleVolumeCommit = () => {
        onVolumeCommit?.(localVolume);
    };

    const handlePlaybackRateChange = (value: number) => {
        setLocalPlaybackRate(value);
        if (onTransformChange) {
            const oldPlaybackRate = clip.playbackRate ?? 1;
            const newPlaybackRate = value;

            // Calculer la nouvelle durée du clip
            const newDuration = (clip.duration * oldPlaybackRate) / newPlaybackRate;
            const durationDelta = newDuration - clip.duration; // positive if slower, negative if faster

            // Recalculer les positions des sous-titres si le clip en a
            let updatedSubtitles = clip.subtitles;
            if (clip.subtitles) {
                updatedSubtitles = {};
                const speedRatio = oldPlaybackRate / newPlaybackRate;

                for (const [lang, subs] of Object.entries(clip.subtitles)) {
                    const subtitleArray = subs as Array<{ id: number | string; start: number; end: number; text: string }>;
                    updatedSubtitles[lang] = subtitleArray.map(sub => {
                        const relativeStart = sub.start - clip.start;
                        const relativeEnd = sub.end - clip.start;
                        const newRelativeStart = relativeStart * speedRatio;
                        const newRelativeEnd = relativeEnd * speedRatio;

                        return {
                            ...sub,
                            start: clip.start + newRelativeStart,
                            end: clip.start + newRelativeEnd
                        };
                    });
                }
            }

            // Update the current clip
            onTransformChange(clip.id, {
                playbackRate: newPlaybackRate,
                duration: newDuration,
                subtitles: updatedSubtitles
            });

            // Ripple: shift ALL subsequent clips (across all tracks) to keep them in sync
            if (durationDelta !== 0 && allClips) {
                const clipEnd = clip.start + clip.duration; // old end
                const subsequentClips = allClips.filter(
                    c => c.id !== clip.id && c.start >= clipEnd - 0.01
                );
                subsequentClips.forEach(c => {
                    // Shift subtitle timecodes along with the clip
                    const updates: Partial<Clip> = { start: c.start + durationDelta };
                    if (c.subtitles) {
                        const shifted: Record<string, any[]> = {};
                        for (const [lang, subs] of Object.entries(c.subtitles)) {
                            shifted[lang] = (subs as any[]).map(s => ({
                                ...s,
                                start: s.start + durationDelta,
                                end: s.end + durationDelta,
                            }));
                        }
                        updates.subtitles = shifted;
                    }
                    onTransformChange(c.id, updates);
                });
            }
        }
    };

    const handleTextChange = (text: string) => {
        setLocalText(text);
        if (onTransformChange) {
            onTransformChange(clip.id, { text });
        }
    };

    const handleTextStyleChange = (updates: Partial<typeof clip.style>) => {
        if (onTransformChange) {
            const newStyle = { ...clip.style, ...updates };
            onTransformChange(clip.id, { style: newStyle });
        }
    };

    const availableFonts = [
        { name: 'LVMH Regular', family: '"LVMH Regular", sans-serif' },
        { name: 'LVMH Bold', family: '"LVMH Bold", sans-serif' },
        { name: 'LVMH Italic', family: '"LVMH Italic", sans-serif' },
        { name: 'LVMH Bold Italic', family: '"LVMH Bold Italic", sans-serif' },
        { name: 'Arial', family: 'Arial, sans-serif' },
        { name: 'Helvetica', family: 'Helvetica, sans-serif' },
        { name: 'Times New Roman', family: '"Times New Roman", serif' },
        { name: 'Georgia', family: 'Georgia, serif' },
        { name: 'Courier New', family: '"Courier New", monospace' },
        { name: 'Verdana', family: 'Verdana, sans-serif' },
        { name: 'Impact', family: 'Impact, sans-serif' },
        { name: 'Comic Sans MS', family: '"Comic Sans MS", cursive' },
        { name: 'Trebuchet MS', family: '"Trebuchet MS", sans-serif' },
        { name: 'Arial Black', family: '"Arial Black", sans-serif' },
        { name: 'Palatino', family: '"Palatino Linotype", serif' },
        { name: 'Garamond', family: 'Garamond, serif' },
        { name: 'Bookman', family: '"Bookman Old Style", serif' },
        { name: 'Tahoma', family: 'Tahoma, sans-serif' },
        { name: 'Lucida', family: '"Lucida Sans Unicode", sans-serif' },
        { name: 'Consolas', family: 'Consolas, monospace' },
        { name: 'Andale Mono', family: '"Andale Mono", monospace' },
    ];

    if (!currentTransform) return null;

    const hasAudioSources = clip.audioSources && clip.audioSources.length > 0;

    return (
        <div className="bg-white text-gray-900 px-6 py-5 space-y-5" aria-label="Contrôles de transformation">
            {/* Transform Section */}
            <section className="space-y-3">
                <h3 className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Transform</h3>

                {/* Position */}
                <div className="space-y-1.5">
                    <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Position</label>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                            <span
                                className="text-xs text-gray-400 cursor-ew-resize select-none hover:text-gray-600"
                                onMouseDown={(e) => handleScrubStart('x', e)}
                            >X</span>
                            <input
                                type="number"
                                value={localPositionX}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setLocalPositionX(val);
                                    handlePositionChange(val, localPositionY);
                                }}
                                className="flex-1 bg-transparent text-xs text-gray-900 text-right border-0 outline-none font-mono"
                            />
                            <span className="text-xs text-gray-400">px</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white border border-gray-300 px-3 py-2">
                            <span
                                className="text-xs text-gray-400 cursor-ew-resize select-none hover:text-gray-600"
                                onMouseDown={(e) => handleScrubStart('y', e)}
                            >Y</span>
                            <input
                                type="number"
                                value={localPositionY}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    setLocalPositionY(val);
                                    handlePositionChange(localPositionX, val);
                                }}
                                className="flex-1 bg-transparent text-xs text-gray-900 text-right border-0 outline-none font-mono"
                            />
                            <span className="text-xs text-gray-400">px</span>
                        </div>
                    </div>
                </div>

                {/* Rotation */}
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Rotation</label>
                        <span className="text-xs text-gray-700 font-mono">{Math.round(localRotation)}°</span>
                    </div>
                    <input
                        type="range"
                        min="-180"
                        max="180"
                        step="1"
                        value={localRotation}
                        onChange={(e) => handleRotationChange(Number(e.target.value))}
                        className="w-full h-1 bg-gray-300 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-gray-600 [&::-webkit-slider-thumb]:rounded-full"
                    />
                </div>

                {/* Scale */}
                <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                        <label className="text-[10px] font-medium uppercase tracking-wider text-gray-500">Scale</label>
                        <span className="text-xs text-gray-700 font-mono">{(localScale * 100).toFixed(0)} %</span>
                    </div>
                    <input
                        type="range"
                        min="0.1"
                        max="3"
                        step="0.01"
                        value={localScale}
                        onChange={(e) => handleScaleChange(Number(e.target.value))}
                        className="w-full h-1 bg-gray-300 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-gray-600 [&::-webkit-slider-thumb]:rounded-full"
                    />
                </div>

                {/* Reset Button */}
                <button
                    onClick={handleReset}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition-colors border border-gray-300"
                >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Réinitialiser
                </button>
            </section>

            {/* Text Controls - Only for text clips */}
            {isTextClip && (
                <>
                    <div className="border-t border-gray-200 pt-8 -mx-6 px-6"></div>

                    {/* Text Content */}
                    <section className="space-y-3">
                        <div className="flex items-center gap-2">
                            <Type className="w-5 h-5 text-gray-600" />
                            <h3 className="text-sm font-semibold text-gray-800">Contenu du texte</h3>
                        </div>
                        <textarea
                            value={localText}
                            onChange={(e) => handleTextChange(e.target.value)}
                            placeholder="Entrez votre texte..."
                            className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                            style={{ fontFamily: localFontFamily }}
                        />
                        <p className="text-xs text-gray-500">Éditez le contenu du texte directement.</p>
                    </section>

                    {/* Font Family */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">Police de caractères</h3>
                        <select
                            value={localFontFamily}
                            onChange={(e) => {
                                setLocalFontFamily(e.target.value);
                                handleTextStyleChange({ fontFamily: e.target.value });
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {availableFonts.map((font) => (
                                <option key={font.family} value={font.family} style={{ fontFamily: font.family }}>
                                    {font.name}
                                </option>
                            ))}
                        </select>
                    </section>

                    {/* Font Size */}
                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800">Taille de police</h3>
                            <span className="text-xs text-gray-500 font-mono">{localFontSize}px</span>
                        </div>
                        <input
                            type="range"
                            min="12"
                            max="200"
                            step="1"
                            value={localFontSize}
                            onChange={(e) => {
                                const size = Number(e.target.value);
                                setLocalFontSize(size);
                                handleTextStyleChange({ fontSize: size });
                            }}
                            className="w-full h-1 bg-gray-300 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-gray-600 [&::-webkit-slider-thumb]:rounded-full"
                        />
                        <input
                            type="number"
                            min="12"
                            max="200"
                            value={localFontSize}
                            onChange={(e) => {
                                const size = Number(e.target.value);
                                setLocalFontSize(size);
                                handleTextStyleChange({ fontSize: size });
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-black"
                        />
                    </section>

                    {/* Color */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">Couleur du texte</h3>
                        <div className="flex gap-2">
                            <input
                                type="color"
                                value={localColor}
                                onChange={(e) => {
                                    setLocalColor(e.target.value);
                                    handleTextStyleChange({ color: e.target.value });
                                }}
                                className="w-16 h-10 rounded-md border border-gray-300 cursor-pointer"
                            />
                            <input
                                type="text"
                                value={localColor}
                                onChange={(e) => {
                                    setLocalColor(e.target.value);
                                    handleTextStyleChange({ color: e.target.value });
                                }}
                                placeholder="#FFFFFF"
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-black font-mono"
                            />
                        </div>
                    </section>

                    {/* Text Alignment */}
                    <section className="space-y-3">
                        <h3 className="text-sm font-semibold text-gray-800">Alignement</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setLocalTextAlign('left');
                                    handleTextStyleChange({ textAlign: 'left' });
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${localTextAlign === 'left' ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                <AlignLeft className="w-4 h-4" />
                                Gauche
                            </button>
                            <button
                                onClick={() => {
                                    setLocalTextAlign('center');
                                    handleTextStyleChange({ textAlign: 'center' });
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${localTextAlign === 'center' ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                <AlignCenter className="w-4 h-4" />
                                Centre
                            </button>
                            <button
                                onClick={() => {
                                    setLocalTextAlign('right');
                                    handleTextStyleChange({ textAlign: 'right' });
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${localTextAlign === 'right' ? 'bg-purple-100 text-purple-700 border-2 border-purple-500' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                            >
                                <AlignRight className="w-4 h-4" />
                                Droite
                            </button>
                        </div>
                    </section>
                </>
            )}

            {/* Image Controls - Allow changing URL for image clips (e.g. panel backgrounds) */}
            {clip.type === 'image' && (
                <>
                    <div className="border-t border-gray-200 pt-8 -mx-6 px-6"></div>
                    <section className="space-y-3">
                        <div className="flex items-center gap-2">
                            <Image className="w-5 h-5 text-gray-600" />
                            <h3 className="text-sm font-semibold text-gray-800">Image de fond</h3>
                        </div>
                        {clip.url && (
                            <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                                <img src={clip.url} alt="Background" className="w-full h-full object-cover" />
                            </div>
                        )}
                        <div className="space-y-2">
                            <p className="text-xs text-gray-500">URL de l'image :</p>
                            <input
                                type="text"
                                value={clip.url || ''}
                                onChange={(e) => {
                                    if (onTransformChange) {
                                        onTransformChange(clip.id, { url: e.target.value } as any);
                                    }
                                }}
                                placeholder="https://..."
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-black font-mono"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <label className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 hover:bg-black text-white text-xs font-medium transition-colors border border-gray-900 cursor-pointer">
                                    <Image className="w-4 h-4" />
                                    Choisir une image
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file && onTransformChange) {
                                                const objectUrl = URL.createObjectURL(file);
                                                onTransformChange(clip.id, { url: objectUrl } as any);
                                            }
                                        }}
                                    />
                                </label>
                                <button
                                    onClick={() => {
                                        if (allClips && onTransformChange) {
                                            const maxTimelineTime = Math.max(...allClips.map(c => c.start + c.duration), 5); // Fallback at 5s
                                            const newDuration = Math.max(0.1, maxTimelineTime - clip.start);
                                            onTransformChange(clip.id, { duration: newDuration });
                                        }
                                    }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-xs font-medium transition-colors border border-indigo-200 cursor-pointer"
                                    title="Ajuste la durée de l'image à la longueur totale de la timeline"
                                >
                                    <Maximize2 className="w-4 h-4" />
                                    Fit timeline
                                </button>
                            </div>
                        </div>
                    </section>
                </>
            )}

            {/* Audio Controls - Only for audio/video clips */}
            {isAudioClip && (
                <>
                    <div className="border-t border-gray-200 pt-8 -mx-6 px-6"></div>

                    {/* Volume Control */}
                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <VolumeIcon className="w-5 h-5 text-gray-600" />
                                <h3 className="text-sm font-semibold text-gray-800">Volume</h3>
                            </div>
                            <span className="text-xs text-gray-500 font-mono">{Math.round(localVolume * 100)}%</span>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="2"
                            step="0.01"
                            value={localVolume}
                            onChange={(e) => handleVolumeChange(Number(e.target.value))}
                            onMouseUp={handleVolumeCommit}
                            onTouchEnd={handleVolumeCommit}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-black [&::-webkit-slider-thumb]:rounded-full"
                        />
                        <p className="text-xs text-gray-500">Ajustez le niveau sonore du clip pour l'équilibrer avec le reste du montage.</p>
                    </section>

                    {/* Playback Speed Control */}
                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800">Vitesse de lecture</h3>
                            <span className="text-xs text-gray-500 font-mono">{localPlaybackRate.toFixed(2)}x</span>
                        </div>
                        <input
                            type="range"
                            min="0.25"
                            max="4"
                            step="0.05"
                            value={localPlaybackRate}
                            onChange={(e) => handlePlaybackRateChange(Number(e.target.value))}
                            className="w-full h-1 bg-gray-300 rounded appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-gray-600 [&::-webkit-slider-thumb]:rounded-full"
                        />
                        <div className="flex gap-2">
                            <button
                                onClick={() => handlePlaybackRateChange(0.5)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                0.5x
                            </button>
                            <button
                                onClick={() => handlePlaybackRateChange(0.75)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                0.75x
                            </button>
                            <button
                                onClick={() => handlePlaybackRateChange(1)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                1x
                            </button>
                            <button
                                onClick={() => handlePlaybackRateChange(1.5)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                1.5x
                            </button>
                            <button
                                onClick={() => handlePlaybackRateChange(2)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                2x
                            </button>
                        </div>
                        <p className="text-xs text-gray-500">Ajustez la vitesse de lecture de l'audio/vidéo (0.25x = très lent, 4x = très rapide).</p>
                    </section>

                    {/* Audio Enhancement */}
                    <section className="space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-800">Amélioration du son</h3>
                            {clip.isEnhancingAudio && (
                                <span className="text-[11px] text-gray-600 font-semibold uppercase tracking-wide">En cours</span>
                            )}
                        </div>
                        <button
                            onClick={() => onEnhanceAudio?.(clip)}
                            disabled={clip.isEnhancingAudio}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-black disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-xs font-medium transition-colors border border-gray-900"
                        >
                            <Sparkles className="w-4 h-4" />
                            {clip.isEnhancingAudio ? 'Amélioration en cours...' : 'Améliorer le son'}
                        </button>

                        {hasAudioSources && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                    onClick={() => setShowAudioSources(!showAudioSources)}
                                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                    <span>Sources audio ({clip.audioSources!.length})</span>
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showAudioSources ? 'rotate-180' : ''}`} />
                                </button>

                                {showAudioSources && (
                                    <div className="divide-y divide-gray-200">
                                        {clip.audioSources!.map((source, idx) => {
                                            const isActive = (clip.activeAudioSource === source.id) ||
                                                (clip.activeAudioSource === source.type && !source.id);
                                            const isOriginal = source.type === 'original';

                                            return (
                                                <div key={source.id || idx} className="p-3 space-y-2 bg-white">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm text-gray-700 font-medium">
                                                                {source.label || (isOriginal ? '🎵 Original' : '✨ Amélioré')}
                                                            </span>
                                                            {source.createdAt && !isOriginal && (
                                                                <span className="text-[10px] text-gray-400">
                                                                    {new Date(source.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {isActive && <span className="text-xs font-bold text-purple-600">ACTIF</span>}
                                                    </div>
                                                    <audio controls className="w-full h-8" onClick={(e) => e.stopPropagation()}>
                                                        <source src={source.url} type="audio/mpeg" />
                                                    </audio>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => onSelectAudioSource?.(clip, source.type, source.id)}
                                                            className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${isActive ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                                                        >
                                                            {isActive ? 'Source active' : 'Activer'}
                                                        </button>
                                                        {!isOriginal && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (source.id) onDeleteAudioSource?.(clip, source.id);
                                                                }}
                                                                className="px-3 py-2 rounded-md bg-red-50 text-red-600 hover:bg-red-100 border border-transparent hover:border-red-200 transition-colors"
                                                                title="Supprimer cette version"
                                                            >
                                                                <X className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {/* Image Mask (Nano Banana) - Only for video/image clips */}
                    {(clip.type === 'video' || clip.type === 'image') && onGenerateImageMask && (
                        <section className="space-y-4 mt-8 pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                                <Image className="w-5 h-5 text-indigo-600" />
                                <h3 className="text-sm font-semibold text-gray-800">Masque Visuel (Nano Banana)</h3>
                            </div>
                            <p className="text-xs text-gray-500">
                                Génère un masque pour incruster ou masquer des éléments via IA.
                            </p>

                            {clip.isGeneratingMask ? (
                                <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-800">Génération du masque...</p>
                                            <p className="text-xs text-gray-600">Patiente quelques instants</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={imageMaskPrompt}
                                            onChange={(e) => setImageMaskPrompt(e.target.value)}
                                            placeholder="ex: Make the sheets in the style of the logo..."
                                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && imageMaskPrompt.trim() && onGenerateImageMask) {
                                                    // Default to generating directly from 1st frame on Enter
                                                    onGenerateImageMask(clip, imageMaskPrompt, false);
                                                    setImageMaskPrompt('');
                                                }
                                            }}
                                        />
                                        <button
                                            onClick={() => {
                                                if (imageMaskPrompt.trim() && onGenerateImageMask) {
                                                    // Generate directly from 1st frame (no crop modal)
                                                    onGenerateImageMask(clip, imageMaskPrompt, false);
                                                    setImageMaskPrompt('');
                                                }
                                            }}
                                            disabled={!imageMaskPrompt.trim() || !onGenerateImageMask}
                                            className="px-3 py-2 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-300 text-white rounded-lg transition-colors flex items-center justify-center shrink-0"
                                            title="Générer automatiquement depuis la 1ère frame"
                                        >
                                            <Wand2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={async () => {
                                                if (onCaptureFrameForMask) {
                                                    const blob = await onCaptureFrameForMask(clip, true);
                                                    if (blob) {
                                                        setMaskSelectionData({ isOpen: true, blob, prompt: imageMaskPrompt, clipId: clip.id });
                                                    }
                                                }
                                            }}
                                            className={`px-3 py-2 ${!onCaptureFrameForMask ? 'bg-indigo-300' : 'bg-indigo-500 hover:bg-indigo-600'} text-white rounded-lg transition-colors flex items-center justify-center shrink-0`}
                                            title="Recadrer la frame actuelle (Canva)"
                                            disabled={!onCaptureFrameForMask}
                                        >
                                            <Camera className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-gray-400 italic">Capture auto de la 1ère frame 🪄, ou recadrage manuel de la frame actuelle 📷.</p>

                                    {/* List masks */}
                                    {clip.imageMasks && clip.imageMasks.length > 0 && (
                                        <div className="border border-gray-200 rounded-lg overflow-hidden mt-4">
                                            <button
                                                onClick={() => setShowImageMasks(!showImageMasks)}
                                                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors"
                                            >
                                                <span>Masques ({clip.imageMasks.length})</span>
                                                <ChevronDown className={`w-4 h-4 transition-transform ${showImageMasks ? 'rotate-180' : ''}`} />
                                            </button>

                                            {showImageMasks && (
                                                <div className="divide-y divide-gray-200">
                                                    {clip.imageMasks.map((mask, idx) => {
                                                        const isActive = clip.activeImageMask === mask.id;
                                                        return (
                                                            <div key={mask.id || idx} className="p-3 bg-white space-y-2">
                                                                <div className="flex gap-3">
                                                                    <div
                                                                        className="w-16 h-16 bg-gray-100 rounded border border-gray-200 overflow-hidden shrink-0 flex items-center justify-center relative cursor-grab active:cursor-grabbing"
                                                                        draggable={true}
                                                                        onDragStart={(e) => {
                                                                            const overlayTransform = computeMaskOverlayTransform(clip, mask);
                                                                            const dragData = {
                                                                                type: 'image',
                                                                                name: `Gen: ${mask.prompt.substring(0, 15)}...`,
                                                                                url: mask.url,
                                                                                duration: clip.duration || 2,
                                                                                isImageMask: true,
                                                                                sourceClipId: clip.id,
                                                                                cropRect: mask.cropRect,
                                                                                transform: overlayTransform
                                                                            };
                                                                            (e.dataTransfer as any).setData('application/json', JSON.stringify(dragData));
                                                                            (e.dataTransfer as any).effectAllowed = 'copy';
                                                                        }}
                                                                        title="Glisser-déposer sur la timeline"
                                                                    >
                                                                        <img src={mask.url} className="max-w-full max-h-full object-contain pointer-events-none" alt="Masque" />
                                                                    </div>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="text-xs font-mono text-gray-600 truncate line-clamp-2" title={mask.prompt}>"{mask.prompt}"</p>
                                                                        {mask.createdAt && (
                                                                            <p className="text-[10px] text-gray-400 mt-1">
                                                                                {new Date(mask.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex gap-2 pt-1">
                                                                    <button
                                                                        onClick={() => onToggleImageMask?.(clip, mask.id, !isActive)}
                                                                        className={`flex-1 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${isActive ? 'bg-indigo-100 text-indigo-700 border border-indigo-300' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                                                                    >
                                                                        {isActive ? 'Masquer' : 'Afficher'}
                                                                    </button>
                                                                    <button
                                                                        onClick={() => onAddImageMaskToTimeline?.(clip, mask)}
                                                                        className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors bg-green-100 text-green-700 hover:bg-green-200 border border-transparent"
                                                                        title="Ajouter comme clip indépendant sur la timeline (t1)"
                                                                    >
                                                                        Ajouter à la timeline
                                                                    </button>
                                                                    <button
                                                                        onClick={() => onDeleteImageMask?.(clip, mask.id)}
                                                                        className="px-3 py-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                                                        title="Supprimer"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {/* Video Enhancement - Only for video clips */}
                    {clip.type === 'video' && onEnhanceVideo && (
                        <section className="space-y-4 mt-8 pt-6 border-t border-gray-200">
                            <div className="flex items-center gap-2">
                                <Wand2 className="w-5 h-5 text-indigo-600" />
                                <h3 className="text-sm font-semibold text-gray-800">Amélioration IA</h3>
                                {clip.duration > 30 && (
                                    <span className="text-xs text-red-500">(max 30s)</span>
                                )}
                            </div>

                            {clip.duration > 30 ? (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-700">
                                        Ce clip dure {Math.round(clip.duration)}s. L'amélioration IA est limitée à 30s max.
                                        Veuillez découper le clip.
                                    </p>
                                </div>
                            ) : clip.isEnhancingVideo ? (
                                <div className="p-4 bg-gray-100 border border-gray-300">
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-800">Amélioration en cours...</p>
                                                <p className="text-xs text-gray-600">Cela peut prendre quelques minutes</p>
                                            </div>
                                        </div>
                                        {onCancelEnhanceVideo && (
                                            <button
                                                onClick={() => onCancelEnhanceVideo(clip)}
                                                className="p-1.5 rounded-full hover:bg-gray-200 text-gray-500 hover:text-red-500 transition-colors"
                                                title="Annuler l'amélioration"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Preset prompts */}
                                    <div className="space-y-2">
                                        <p className="text-xs text-gray-500">Presets rapides (cliquer = lancer) :</p>
                                        <div className="flex flex-wrap gap-2">
                                            {VIDEO_ENHANCEMENT_PRESETS.map(preset => (
                                                <button
                                                    key={preset.id}
                                                    onClick={() => {
                                                        // Auto-trigger the enhancement on preset click
                                                        onEnhanceVideo(clip, preset.prompt);
                                                    }}
                                                    className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-indigo-100 hover:border-indigo-300 border border-gray-300 transition-colors"
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Custom prompt */}
                                    <div className="space-y-2">
                                        <p className="text-xs text-gray-500">Ou prompt personnalisé :</p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={customVideoPrompt}
                                                onChange={(e) => setCustomVideoPrompt(e.target.value)}
                                                placeholder="ex: remove the coffee cup"
                                                className="flex-1 px-3 py-2 text-sm border border-gray-300 focus:outline-none focus:border-black"
                                            />
                                            <button
                                                onClick={() => {
                                                    if (customVideoPrompt.trim()) {
                                                        onEnhanceVideo(clip, customVideoPrompt.trim());
                                                        setCustomVideoPrompt('');
                                                    }
                                                }}
                                                disabled={!customVideoPrompt.trim()}
                                                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <Wand2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Video versions */}
                            {clip.videoVersions && clip.videoVersions.length > 0 && (
                                <div className="space-y-2 mt-4">
                                    <button
                                        onClick={() => setShowVideoVersions(!showVideoVersions)}
                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                                    >
                                        <Video className="w-4 h-4" />
                                        <span>{clip.videoVersions.length + 1} version(s)</span>
                                        <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showVideoVersions ? 'rotate-180' : ''}`} />
                                    </button>

                                    {showVideoVersions && (
                                        <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                                            {/* Original version */}
                                            <div className="p-3 bg-white">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm text-gray-700 font-semibold mb-2 block">📹 Original</span>
                                                    {!clip.activeVideoVersion && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                                                </div>

                                                {/* Video preview for Original */}
                                                {(() => {
                                                    const originalUrl = clip.videoVersions?.find(v => v.id === 'original')?.url || (clip.activeVideoVersion ? undefined : clip.url);
                                                    return originalUrl ? (
                                                        <div className="relative aspect-video bg-black rounded-lg overflow-hidden mt-1 mb-2">
                                                            <video
                                                                src={originalUrl}
                                                                className="w-full h-full object-cover"
                                                                muted
                                                                loop
                                                                onLoadedMetadata={(e) => {
                                                                    e.currentTarget.currentTime = clip.startOffset || 0;
                                                                }}
                                                                onTimeUpdate={(e) => {
                                                                    const video = e.currentTarget;
                                                                    const start = clip.startOffset || 0;
                                                                    const end = start + clip.duration;
                                                                    if (video.currentTime >= end || video.currentTime < start) {
                                                                        video.currentTime = start;
                                                                    }
                                                                }}
                                                                onMouseOver={(e) => {
                                                                    e.currentTarget.currentTime = clip.startOffset || 0;
                                                                    e.currentTarget.play();
                                                                }}
                                                                onMouseOut={(e) => {
                                                                    e.currentTarget.pause();
                                                                    e.currentTarget.currentTime = clip.startOffset || 0;
                                                                }}
                                                            />
                                                            <div className="absolute bottom-1 right-2 text-[10px] text-white/70 bg-black/40 px-1 rounded pointer-events-none">Hover to play</div>
                                                        </div>
                                                    ) : null;
                                                })()}

                                                {clip.activeVideoVersion && (
                                                    <button
                                                        onClick={() => onSelectVideoVersion?.(clip, undefined)}
                                                        className="w-full text-sm font-medium px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                                    >
                                                        Revenir à l'original
                                                    </button>
                                                )}
                                            </div>

                                            {/* Enhanced versions */}
                                            {clip.videoVersions.filter(v => v.id !== 'original').map((version) => {
                                                const isActive = clip.activeVideoVersion === version.id;
                                                return (
                                                    <div key={version.id} className="p-3 bg-white">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div>
                                                                <span className="text-sm text-gray-700 font-semibold">✨ {version.prompt.slice(0, 30)}...</span>
                                                                <p className="text-[10px] text-gray-400">{new Date(version.createdAt).toLocaleTimeString()}</p>
                                                            </div>
                                                            {isActive && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                                                        </div>

                                                        {/* Video preview for enhanced version */}
                                                        <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-2">
                                                            <video
                                                                src={version.url}
                                                                className="w-full h-full object-cover"
                                                                muted
                                                                loop
                                                                onMouseOver={(e) => e.currentTarget.play()}
                                                                onMouseOut={(e) => e.currentTarget.pause()}
                                                            />
                                                            <div className="absolute bottom-1 right-2 text-[10px] text-white/70 bg-black/40 px-1 rounded pointer-events-none">Hover to play</div>
                                                        </div>

                                                        {!isActive && (
                                                            <button
                                                                onClick={() => onSelectVideoVersion?.(clip, version.id)}
                                                                className="w-full text-sm font-medium px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                                                            >
                                                                Activer cette version
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    {/* NVIDIA Eye Contact - Only for video clips */}
                    {clip.type === 'video' && (
                        <section className="space-y-4 mt-8 pt-6 border-t border-gray-200 opacity-60 pointer-events-none relative select-none">
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 flex items-center justify-center bg-gray-100 rounded-full">
                                    <span className="text-[10px] transform scale-75 grayscale">👁️</span>
                                </div>
                                <h3 className="text-sm font-semibold text-gray-500">Contact Visuel (beta)</h3>
                                <span className="px-1.5 py-0.5 text-[9px] font-bold text-white bg-gray-400 rounded">COMING SOON</span>
                            </div>

                            <p className="text-xs text-gray-400">
                                Redirige le regard vers la caméra grâce à l'IA.
                            </p>

                            <button
                                disabled
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-400 text-sm font-semibold rounded-lg cursor-not-allowed border border-gray-200"
                            >
                                <span className="text-lg opacity-50">👁️</span>
                                Appliquer Eye Contact
                            </button>
                        </section>
                    )}
                </>
            )}

            <ImageMaskSelectionModal
                isOpen={maskSelectionData.isOpen}
                imageBlob={maskSelectionData.blob}
                initialPrompt={maskSelectionData.prompt}
                onClose={() => setMaskSelectionData({ ...maskSelectionData, isOpen: false })}
                onConfirm={(croppedBlob, finalPrompt, cropRect) => {
                    if (onGenerateImageMask && maskSelectionData.clipId === clip.id) {
                        onGenerateImageMask(clip, finalPrompt, croppedBlob, cropRect);
                        setImageMaskPrompt('');
                    }
                }}
            />
        </div>
    );
};

export default TransformControls;
